/**
 * Criar conta: nome, e-mail e senha, ou Google.
 *
 * Antes a conta nascia do link mágico, e a pessoa só via o app depois de
 * abrir o e-mail. Aqui ela entra direto: o teste de 14 dias começa no mesmo
 * toque.
 */
import { ApiError } from '@lindeza/core';
import { useRuntime } from '@lindeza/core/hooks';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { IconChevronLeft, IconKeyRound } from '../../components/Icons';
import { Brand, EntrarShell, useDesktop } from './EntrarShell';
import { GoogleButton, googleEnabled } from './GoogleButton';
import './entrar.css';

/** O mesmo mínimo que a API exige; conferir aqui evita a ida e volta. */
const MIN_SENHA = 10;

function senhaFraca(senha: string): string | null {
  if (senha.length < MIN_SENHA) return `Use pelo menos ${MIN_SENHA} caracteres.`;
  if (!/[a-zA-Z]/.test(senha) || !/\d/.test(senha)) return 'Misture letras e números.';
  return null;
}

export function Cadastro() {
  const rt = useRuntime();
  const navigate = useNavigate();
  const desktop = useDesktop();
  const [params] = useSearchParams();
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function criar(e?: FormEvent) {
    e?.preventDefault();
    setError(null);

    if (nome.trim().length < 2) {
      setError('Como podemos te chamar?');
      return;
    }
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setError('Confira o e-mail.');
      return;
    }
    const fraca = senhaFraca(senha);
    if (fraca) {
      setError(fraca);
      return;
    }

    setBusy(true);
    try {
      await rt.register({
        name: nome.trim(),
        email,
        password: senha,
        // De onde ela veio, se o link trouxe: guardado só na criação.
        ...(params.get('utm_source') ? { source: params.get('utm_source') as string } : {}),
        ...(params.get('utm_campaign') ? { campaign: params.get('utm_campaign') as string } : {}),
        ...(params.get('ref') ? { ref: params.get('ref') as string } : {}),
      });
      navigate('/', { replace: true });
    } catch (err) {
      setError(
        err instanceof ApiError && err.isNetwork
          ? 'Sem internet. Para criar sua conta, conecte-se uma vez.'
          : // 422 aqui é específico: e-mail já usado, ou senha fraca pela regra do servidor.
            (err as Error).message,
      );
    } finally {
      setBusy(false);
    }
  }

  const H = desktop ? 'h2' : 'h1';

  const form = (
    <form className="en-form" onSubmit={criar} noValidate>
      {!desktop && (
        <header className="en-bar">
          <button type="button" className="en-back" onClick={() => navigate('/')}>
            <IconChevronLeft strokeWidth={2.5} /> Voltar
          </button>
        </header>
      )}

      <div className={desktop ? undefined : 'en-pad'}>
        <H className={desktop ? 'en-dk-h' : 'en-h'}>Criar conta</H>
        <p className={desktop ? 'en-dk-sub' : 'en-sub'}>14 dias grátis, sem cartão.</p>

        <label className="en-lbl en-lbl-top" htmlFor="nome">
          Seu nome
        </label>
        <input
          id="nome"
          className="en-in"
          type="text"
          autoComplete="name"
          autoFocus
          value={nome}
          onChange={(e) => setNome(e.target.value)}
          placeholder="Ana Ribeiro"
        />

        <label className="en-lbl en-lbl-top" htmlFor="email">
          Seu e-mail
        </label>
        <input
          id="email"
          className="en-in"
          type="email"
          inputMode="email"
          autoComplete="email"
          autoCapitalize="none"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="voce@email.com"
        />

        <label className="en-lbl en-lbl-top" htmlFor="senha">
          Crie uma senha
        </label>
        <input
          id="senha"
          className="en-in"
          type="password"
          autoComplete="new-password"
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          placeholder="••••••••••"
        />
        <p className="en-hint">Pelo menos {MIN_SENHA} caracteres, com letras e números.</p>

        {error && (
          <p className="en-error" role="alert">
            {error}
          </p>
        )}

        <button className={`btn btn--primary ${desktop ? 'en-dk-cta' : 'en-cta'}`} disabled={busy}>
          {busy ? 'Criando…' : 'Criar minha conta'}
        </button>

        {googleEnabled() && (
          <>
            <div className="en-ou" role="separator">
              <span>ou</span>
            </div>
            <GoogleButton label="Criar conta com o Google" />
          </>
        )}

        <p className="en-note">
          <IconKeyRound />
          <span>
            Já tem conta? <Link to="/entrar">Entrar</Link>
          </span>
        </p>
      </div>
    </form>
  );

  if (desktop) return <EntrarShell>{form}</EntrarShell>;
  return (
    <main className="en-page">
      <header className="en-bar" style={{ paddingLeft: 20 }}>
        <Brand />
      </header>
      {form}
    </main>
  );
}
